import React, { createContext, useState, useEffect, ReactNode } from "react";
import axios from "axios";
import { useAlert } from "../AlertContext";
import { AuthContext } from "../AuthContext";
import { UserContext } from "../UserContext";

const server_url = process.env.REACT_APP_BACKEND_URL;

interface User {
  id: number;
  name: string;
  mail: string | null;
  phone: string | null;
  role: string;
}

export interface Team {
  team_id: number;
  name: string;
  logo_url: string;
  name_short: string;
  is_active: boolean;
  accepted: boolean;
  created_at: string;
  captain: User;
  players: User[];
}

interface DashboardContextType {
  loading: boolean;
  requests: Team[];
  acceptedTeams: Team[];
  myTeam: Team | null;
  loadRequests: () => Promise<void>;
  handleWithdraw: () => Promise<void>;
  getPlayersFromTeam: (team_id?: number) => User[];
  getCaptainsFromTeam: (team_id?: number) => User[];
  getTeamJoinRequests: (team_id?: number) => User[];
}

export const DashboardContext = createContext<DashboardContextType>({
  loading: false,
  requests: [],
  acceptedTeams: [],
  myTeam: null,
  loadRequests: async () => {},
  handleWithdraw: async () => {},
  getPlayersFromTeam: () => [],
  getCaptainsFromTeam: () => [],
  getTeamJoinRequests: () => [],
});

interface DashboardProviderProps {
  children: ReactNode;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
}) => {
  const { role, session } = React.useContext(AuthContext);
  const { fetchUserData } = React.useContext(UserContext);
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<Team[]>([]);
  const [acceptedTeams, setAcceptedTeams] = useState<Team[]>([]);
  const [myTeam, setMyTeam] = useState<Team | null>(null);

  const loadRequests = async () => {
    if (session) {
      setLoading(true);
      if (role === "ADMIN") {
        try {
          const response = await axios.get(`${server_url}/getTeamRequests`);
          const myTeamResponse = await axios.post(`${server_url}/getMyTeam`, {
            access_token: session.access_token,
          });
          if (myTeamResponse.data.status) {
            setMyTeam(myTeamResponse.data.data);
          }
          if (response.data.status) {
            setRequests(response.data.data);
          } else {
            showAlert("error", response.data.error);
          }
          const responseAcc = await axios.get(`${server_url}/getAcceptedTeams`);
          if (responseAcc.data.status) {
            setAcceptedTeams(responseAcc.data.data);
          } else {
            showAlert("error", responseAcc.data.error);
          }
        } catch (error) {
          console.error("Error:", error);
          showAlert("error", "Unexpected Error when fetching Requests!");
        } finally {
          setLoading(false);
        }
      } else if (role === "CAPTAIN" || role === "CAPTAIN_PENDING") {
        try {
          const response = await axios.post(`${server_url}/getMyTeam`, {
            access_token: session.access_token,
          });
          if (response.data.status) {
            setMyTeam(response.data.data);
          } else {
            showAlert("error", response.data.error);
          }
        } catch (error) {
          console.error("Error getting team:", error);
          showAlert("error", "Unexpected Error when fetching Team!");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
  };

  const handleWithdraw = async () => {
    if (session) {
      try {
        setLoading(true);
        const response = await axios.post(`${server_url}/joinTeam`, {
          access_token: session.access_token,
          team_id: -1,
        });

        if (response.data.status) {
          showAlert("success", "Successful!");
          await fetchUserData();
        } else {
          showAlert("error", response.data.error);
        }
      } catch (err) {
        console.error("Error withdrawing the request:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  const getPlayersFromTeam = (team_id: number = -1): User[] => {
    if (role === "CAPTAIN" || role === "CAPTAIN_PENDING") {
      if (myTeam && myTeam.players) {
        return myTeam.players.filter((player) => player.role === "PLAYER");
      }
      return [];
    } else if (role === "ADMIN") {
      const t = acceptedTeams.find((team) => team.team_id === team_id);
      if (t && t.players) {
        return t.players.filter((player) => player.role === "PLAYER");
      }
    }
    return [];
  };
  const getCaptainsFromTeam = (team_id: number = -1): User[] => {
    if (role === "CAPTAIN" || role === "CAPTAIN_PENDING") {
      if (myTeam && myTeam.players) {
        return myTeam.players.filter((player) => player.role === "CAPTAIN");
      }
      return [];
    } else if (role === "ADMIN") {
      const t = acceptedTeams.find((team) => team.team_id === team_id);
      if (t && t.players) {
        return t.players.filter((player) => player.role === "CAPTAIN");
      }
    }
    return [];
  };

  const getTeamJoinRequests = (team_id: number = -1) => {
    if (role === "CAPTAIN" || role === "CAPTAIN_PENDING") {
      if (myTeam && myTeam.players) {
        return myTeam.players.filter((player) => player.role === "USER");
      }
      return [];
    } else if (role === "ADMIN") {
      const t = acceptedTeams.find((team) => team.team_id === team_id);
      if (t && t.players) {
        return t.players.filter((player) => player.role === "USER");
      }
    }
    return [];
  };

  useEffect(() => {
    loadRequests();
  }, [role, session]);

  return (
    <DashboardContext.Provider
      value={{
        loading,
        requests,
        acceptedTeams,
        myTeam,
        loadRequests,
        handleWithdraw,
        getPlayersFromTeam,
        getCaptainsFromTeam,
        getTeamJoinRequests,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
